/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import classnames from 'classnames';

import { createRef } from 'react';

import { IHobartPartstreamPartsData } from './hobart-partstream-parts.data';
import { IHobartPartstreamPartsProps } from './hobart-partstream-parts.props.autogenerated';

import { getCartState } from '@msdyn365-commerce/global-state';
import { AddByIdItems, CartUtilities } from '../../utilities/cart-utils';

import { clsHelper } from '../../utilities/class-name-helper';

import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import { RichTextComponent } from '@msdyn365-commerce/core';


//==============================================================================
// CLASS NAME UTILITY
//==============================================================================
const BASE_CLASS = 'hobart-partstream-parts';
const cls = (fragment: string) => clsHelper(BASE_CLASS, fragment);

/**
 *
 * HobartPartstreamParts component
 * @extends {React.PureComponent<IHobartPartstreamPartsProps<IHobartPartstreamPartsData>>}
 */

// Add PartStreamAddToCart for PartStream third party use
declare global {
    interface Window {
        PartStreamAddToCart: (params: string) => void;
    }
}

@observer
class HobartPartstreamParts extends React.PureComponent<IHobartPartstreamPartsProps<IHobartPartstreamPartsData>> {
    public scriptRef = createRef();
    @observable private isModalOpen: boolean = false;
    @observable private addToCartError: string = '';

    public componentDidMount(): void {
        // PartStream 3rd party script config
        const script = document.createElement('script');
        const partStreamUrl = this.props.config.partStreamURL;

        if (partStreamUrl) {
            script.src = partStreamUrl;
            script.async = true;

            if (this.scriptRef.current) {
                // @ts-ignore
                this.scriptRef.current.appendChild(script);
            }

            // Adding PartStreamAddToCart to the global namespace for PartStream 3rd party call
            window.PartStreamAddToCart = this.PartStreamAddToCart.bind(this);
        }
    }

    public render(): JSX.Element | null {
        const { config } = this.props;

        const style: { [key: string]: string } = {};

        return (
            <div className={classnames(BASE_CLASS, config.className)} style={style}>
                {/*@ts-ignore*/}
                <div ref={this.scriptRef} />
                {this.renderErrorModal()}
            </div>
        );
    }

    // Function for 3rd party PartStream use
    public async PartStreamAddToCart(paramString: string) {
        const params = Object.fromEntries(new URLSearchParams(decodeURI(paramString)));

        try {
            const cartState = await getCartState(this.props.context.actionContext);

            if (params && cartState) {
                const sku = params['arisku'];
                const qty = Number(params['ariqty']);

                if (sku && qty && qty > 0) {
                    const item: AddByIdItems = [{
                        Id: sku,
                        Quantity: qty
                    }];
                    //Retrieve cart status so that if adding to cart fails, it will display error modal.
                    const cartAddStatus = await CartUtilities.addToCartByIds(cartState, this.props.context, item);
                    if (cartAddStatus.status === 'FAILED') {
                        throw new Error();
                    }
                }

            }
        } catch (error) {
            this.props.context.telemetry.error(`Failed to execute PartStreamAddToCart due to error: ${this.addToCartError}`);
            this.isModalOpen = true;
        }
    }
    private handleCloseModal = () => {
        this.isModalOpen = false;
    };

    private renderErrorModal() {
        return (
            <Modal isOpen={this.isModalOpen} className={cls('modal')} onClosed={this.handleCloseModal}>
                <ModalHeader className={cls('modal-header')}>
                    <RichTextComponent text={this.props.config.errorModalHeader} />
                    <button className={cls('close-button')} onClick={this.handleCloseModal}>&times;</button>
                </ModalHeader>
                <ModalBody className={cls('modal-body')}>
                    <div className={cls('modal-content')}>
                        <RichTextComponent text={this.props.config.errorModalContent} />
                    </div>
                </ModalBody>
            </Modal>
        );
    }
}

export default HobartPartstreamParts;
